import React from 'react';
import { Link } from 'react-router-dom';

export enum CompanyLogoColor {
  light = '#FFFFFF',
  dark = '#000000',
}

export enum CompanyLogoSize {
  sm = 'sm',
  md = 'md',
}

export interface CompanyLogoProps {
  /**
   * Define if Logo is and link.
   */
  hasLink?: boolean;
  /**
   * Specify the label of the logo.
   */
  arialLabel?: string;
  /**
   * Specify the color of the logo.
   */
  color?: CompanyLogoColor;
  /**
   * Specify an optional className to be added to the component
   */
  className?: string;
  /**
   * Specify the size of the logos.
   * @default md
   */
  size?: CompanyLogoSize;
}

const CompayLogoSizes: Record<CompanyLogoSize, { width: number; height: number }> = {
  [CompanyLogoSize.md]: {
    width: 73,
    height: 26,
  },
  [CompanyLogoSize.sm]: {
    width: 44,
    height: 16,
  },
};

/**
 * The company logo is a visual representation of a brand.
 * @author Carlos Cuatin<carlos.cuatin@evacenter.com>
 * Created at 2023-07-28
 */
export const CompanyLogo = ({
  className,
  arialLabel,
  hasLink = true,
  size = CompanyLogoSize.md,
  color = CompanyLogoColor.light,
}: CompanyLogoProps) => {
  const logo = (
    <svg
      fill={color}
      width={CompayLogoSizes[size].width}
      height={CompayLogoSizes[size].height}
      viewBox="0 0 110 39"
      preserveAspectRatio="xMinYMin meet"
      xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M31.654 34.3893C33.9288 36.8811 36.7374 38.1279 40.0771 38.1279C41.747 38.1279 43.2859 37.7582 44.6938 37.0205C46.101 36.2836 47.1815 35.3902 47.9354 34.3402V37.6352H53.3379V0.75H47.9354V16.341C47.1815 15.2918 46.101 14.3975 44.6938 13.6598C43.2859 12.923 41.747 12.5533 40.0771 12.5533C36.7374 12.5533 33.9288 13.8 31.654 16.291C29.3776 18.7836 28.2406 21.8 28.2406 25.3402C28.2406 28.8811 29.3776 31.8984 31.654 34.3893ZM41.0594 33.2098C38.9311 33.2098 37.1794 32.4557 35.8034 30.9467C34.429 29.4393 33.7414 27.5705 33.7414 25.3402C33.7414 23.1115 34.429 21.2426 35.8034 19.7336C37.1794 18.227 38.9311 17.4713 41.0594 17.4713C43.1877 17.4713 44.9386 18.227 46.3146 19.7336C47.6898 21.2426 48.3774 23.1115 48.3774 25.3402C48.3774 27.5705 47.6898 29.4393 46.3146 30.9467C44.9386 32.4557 43.1877 33.2098 41.0594 33.2098ZM3.60989 34.5369C6.01648 36.9303 9.08611 38.1279 12.8188 38.1279C17.9266 38.1279 21.6593 36.2262 24.0168 32.4221L19.8912 29.4713C18.3523 31.9631 16.0112 33.2098 12.8679 33.2098C10.7724 33.2098 9.0198 32.5951 7.61269 31.3656C6.20475 30.1361 5.43448 28.5205 5.30432 26.5213H24.6062C24.6381 25.9967 24.6553 25.4066 24.6553 24.7508C24.6553 20.9467 23.5338 17.9631 21.291 15.8C19.0473 13.6352 16.224 12.5533 12.8188 12.5533C9.11803 12.5533 6.05659 13.7754 3.63444 16.218C1.21066 18.6607 0 21.7008 0 25.3402C0 29.0779 1.2033 32.1443 3.60989 34.5369ZM19.2036 22.5861H5.54989C5.91006 20.8492 6.75319 19.5049 8.07927 18.5533C9.40535 17.6033 10.9197 17.127 12.6223 17.127C14.2595 17.127 15.692 17.6123 16.9198 18.5779C18.1477 19.5459 18.9089 20.882 19.2036 22.5861ZM71.1418 38.25C67.4091 38.25 64.3395 37.0525 61.9329 34.659C59.5263 32.2664 58.323 29.2 58.323 25.4623C58.323 21.8229 59.5337 18.7828 61.9574 16.3402C64.3796 13.8975 67.441 12.6754 71.1418 12.6754C74.547 12.6754 77.3703 13.7574 79.614 15.9221C81.8568 18.0852 82.9783 21.0689 82.9783 24.8729C82.9783 25.5287 82.9611 26.1189 82.9292 26.6434H63.6273C63.7575 28.6426 64.5278 30.2582 65.9357 31.4877C67.3428 32.7172 69.0954 33.332 71.1909 33.332C74.3342 33.332 76.6753 32.0852 78.2142 29.5934L82.3398 32.5443C79.9823 36.3484 76.2496 38.25 71.1418 38.25ZM63.8729 22.7082H77.5266C77.2319 21.0041 76.4707 19.668 75.2428 18.7C74.015 17.7344 72.5825 17.2492 70.9453 17.2492C69.2427 17.2492 67.7284 17.7254 66.4023 18.6754C65.0762 19.627 64.2331 20.9713 63.8729 22.7082ZM92.5801 13.0451H87.1775V37.6352H92.5801V25.5369C92.5801 22.9795 93.1686 20.9959 94.3482 19.5852C95.5261 18.177 97.0495 17.4705 98.9158 17.4705C100.487 17.4705 101.731 18.0205 102.648 19.1189C103.564 20.218 104.023 21.75 104.023 23.718V37.6352H109.426V22.832C109.426 19.7172 108.574 17.2262 106.872 15.3557C105.169 13.4877 102.893 12.5525 100.045 12.5525C96.6066 12.5525 94.1182 13.832 92.5801 16.3893V13.0451Z"
      />
    </svg>
  );

  if (!hasLink) {
    return (
      <div className={className} aria-label={arialLabel}>
        {logo}
      </div>
    );
  }

  return (
    <Link className={className} aria-label={arialLabel} rel="noopener noreferrer" to="/">
      {logo}
    </Link>
  );
};
